import { createReducer, on } from "@ngrx/store";
import { Contact, Customer, InventoryTransaction, MergeArrayValues, MetalsHardwareItem, MetalsLaserMaterial, MetalsProduct, MetalsProductSetup, MetalsProductVersion, MetalsProject, MetalsVendorHardwareData, Resource, SaveStatus, StampingStation, StampingTask, Tag, ToolRoomMachine } from "@prism/prism-shared";
import { MetalsActions } from ".";



export interface MetalsState {
    hardwareItems: MetalsHardwareItem[];
    inventoryTransactions: InventoryTransaction[]
    vendorHardwareData: MetalsVendorHardwareData[];
    contacts: Contact[];
    tags: Tag[];
    // workOrderSteps: MetalsWorkOrderStep[];
    products: MetalsProduct[];
    productVersions: MetalsProductVersion[];
    stampingStations: StampingStation[];
    stampingTasks: StampingTask[];
    customers: Customer[];
    metalsLaserMaterials: MetalsLaserMaterial[];
    productSetups: MetalsProductSetup[];
    toolRoomMachines: ToolRoomMachine[];

    loadingState: number;
    error: any;
}

export const initialState: MetalsState = {
    hardwareItems: [],
    inventoryTransactions: [],
    vendorHardwareData: [],
    contacts: [],
    tags: [],

    products: [],
    productVersions: [],
    stampingStations: [],
    stampingTasks: [],
    customers: [],
    metalsLaserMaterials: [],
    productSetups: [],
    toolRoomMachines: [],

    loadingState: 0,
    error: null,
};

export const metalsReducer = createReducer(
    initialState,
    // =========================================================
    on(MetalsActions.LoadHardwareItems.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(MetalsActions.LoadHardwareItems.success, (state, { hardwareItems }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        hardwareItems,
        error: null
    })),
    on(MetalsActions.LoadHardwareItems.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.LoadHardwareItemById.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(MetalsActions.LoadHardwareItemById.success, (state, { hardwareItem }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        hardwareItems: MergeArrayValues(state.hardwareItems, hardwareItem),
        error: null
    })),
    on(MetalsActions.LoadHardwareItemById.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.LoadHardwareItemsByIds.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(MetalsActions.LoadHardwareItemsByIds.success, (state, { hardwareItems }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        hardwareItems,
        error: null
    })),
    on(MetalsActions.LoadHardwareItemsByIds.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.LoadHardwareItemsByType.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(MetalsActions.LoadHardwareItemsByType.success, (state, { hardwareItems }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        hardwareItems: MergeArrayValues(state.hardwareItems, hardwareItems),
        error: null
    })),
    on(MetalsActions.LoadHardwareItemsByType.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.LoadMetalsLaserMaterials.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(MetalsActions.LoadMetalsLaserMaterials.success, (state, { metalsLaserMaterials }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        metalsLaserMaterials: MergeArrayValues(state.metalsLaserMaterials, metalsLaserMaterials),
        error: null
    })),
    on(MetalsActions.LoadMetalsLaserMaterials.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.SetHardwareItem.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(MetalsActions.SetHardwareItem.success, (state, { hardwareItem }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        hardwareItems: MergeArrayValues(state.hardwareItems, hardwareItem),
        error: null
    })),
    on(MetalsActions.SetHardwareItem.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.LoadContacts.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(MetalsActions.LoadContacts.success, (state, { contacts }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        contacts,
        error: null
    })),
    on(MetalsActions.LoadContacts.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.LoadContactById.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(MetalsActions.LoadContactById.success, (state, { contact }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        contacts: MergeArrayValues(state.contacts, contact),
        error: null
    })),
    on(MetalsActions.LoadContactById.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.SetContact.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(MetalsActions.SetContact.success, (state, { contact }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        contacts: MergeArrayValues(state.contacts, contact),
        error: null
    })),
    on(MetalsActions.SetContact.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.LoadTags.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(MetalsActions.LoadTags.success, (state, { tags }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        tags,
        error: null
    })),
    on(MetalsActions.LoadTags.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.SetProduct.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(MetalsActions.SetProduct.success, (state, { product }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        products: MergeArrayValues(state.products, product),
        error: null
    })),
    on(MetalsActions.SetProduct.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.SetProductVersion.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(MetalsActions.SetProductVersion.success, (state, { productVersion }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        productVersions: MergeArrayValues(state.productVersions, productVersion),
        error: null
    })),
    on(MetalsActions.SetProductVersion.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.LoadProducts.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(MetalsActions.LoadProducts.success, (state, { products }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        products,
        error: null
    })),
    on(MetalsActions.LoadProducts.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.LoadReleasedProductVersions.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(MetalsActions.LoadReleasedProductVersions.success, (state, { releasedProductVersions }): MetalsState => ({
        ...state,
        productVersions: MergeArrayValues(state.productVersions, releasedProductVersions),
        loadingState: state.loadingState - 1,
        error: null
    })),
    on(MetalsActions.LoadReleasedProductVersions.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.LoadReleasedAndDraftProductVersions.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(MetalsActions.LoadReleasedAndDraftProductVersions.success, (state, { releasedAndDraftProductVersions }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        productVersions: MergeArrayValues(state.productVersions, releasedAndDraftProductVersions),
        error: null,
    })),
    on(MetalsActions.LoadReleasedAndDraftProductVersions.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.LoadProductsByIds.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(MetalsActions.LoadProductsByIds.success, (state, { products }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        products: MergeArrayValues(state.products, products),
        error: null
    })),
    on(MetalsActions.LoadProductsByIds.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.LoadProductVersionsByIds.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(MetalsActions.LoadProductVersionsByIds.success, (state, { productVersions }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        productVersions,
        error: null,
    })),
    on(MetalsActions.LoadProductVersionsByIds.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    // =========================================================
    on(MetalsActions.SetBillOfMaterialsLineItems.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(MetalsActions.SetBillOfMaterialsLineItems.success, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error: null
    })),
    on(MetalsActions.SetBillOfMaterialsLineItems.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.ClearState, (): MetalsState => ({
        ...initialState
    })),
    // =========================================================
    on(MetalsActions.LoadVendorHardwareData.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(MetalsActions.LoadVendorHardwareData.success, (state, { vendorHardwareData }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        vendorHardwareData,
        error: null
    })),
    on(MetalsActions.LoadVendorHardwareData.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.LoadVendorHardwareDataByHardwareItemId.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(MetalsActions.LoadVendorHardwareDataByHardwareItemId.success, (state, { vendorHardwareData }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        vendorHardwareData: MergeArrayValues(state.vendorHardwareData, vendorHardwareData),
        error: null
    })),
    on(MetalsActions.LoadVendorHardwareDataByHardwareItemId.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.LoadVendorHardwareDataByHardwareItemIds.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(MetalsActions.LoadVendorHardwareDataByHardwareItemIds.success, (state, { vendorHardwareData }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        vendorHardwareData: MergeArrayValues(state.vendorHardwareData, vendorHardwareData),
        error: null
    })),
    on(MetalsActions.LoadVendorHardwareDataByHardwareItemIds.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.SetHardwareInventoryTransaction.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    // original
    on(MetalsActions.SetHardwareInventoryTransaction.success, (state, { transaction }): MetalsState => {
        let updatedHardwareItem = { ...state.hardwareItems.find(h => h.id === transaction.entityId) } as MetalsHardwareItem
        updatedHardwareItem.qtyOnHand += transaction.change
        return {
            ...state,
            inventoryTransactions: MergeArrayValues(state.inventoryTransactions, transaction),
            hardwareItems: MergeArrayValues(state.hardwareItems, updatedHardwareItem),
            loadingState: state.loadingState - 1,
            error: null
        }
    }),
    on(MetalsActions.SetHardwareInventoryTransaction.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.LoadRecentHardwareInventoryTransactions.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(MetalsActions.LoadRecentHardwareInventoryTransactions.success, (state, { transactions }): MetalsState => ({
        ...state,
        inventoryTransactions: MergeArrayValues(state.inventoryTransactions, transactions),
        loadingState: state.loadingState - 1,
        error: null,
    })),
    on(MetalsActions.LoadRecentHardwareInventoryTransactions.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.SetStampingStation.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(MetalsActions.SetStampingStation.success, (state, {
        station
    }): MetalsState => ({
        ...state,
        stampingStations: MergeArrayValues(state.stampingStations, station),
        loadingState: state.loadingState - 1,
    })),
    on(MetalsActions.SetStampingStation.failure, (state, {
        error,
    }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.SetStampingTask.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(MetalsActions.SetStampingTask.success, (state, {
        task
    }): MetalsState => ({
        ...state,
        stampingTasks: MergeArrayValues(state.stampingTasks, task),
        loadingState: state.loadingState - 1,
    })),
    on(MetalsActions.SetStampingTask.failure, (state, {
        error,
    }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.LoadActiveStampingTasks.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(MetalsActions.LoadActiveStampingTasks.success, (state, {
        tasks
    }): MetalsState => ({
        ...state,
        stampingTasks: tasks,
        loadingState: state.loadingState - 1,
    })),
    on(MetalsActions.LoadActiveStampingTasks.failure, (state, {
        error,
    }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.LoadStampingStations.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(MetalsActions.LoadStampingStations.success, (state, {
        stations
    }): MetalsState => ({
        ...state,
        stampingStations: stations,
        loadingState: state.loadingState - 1,
    })),
    on(MetalsActions.LoadStampingStations.failure, (state, {
        error,
    }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.SetVendorHardwareData.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null
    })),
    on(MetalsActions.SetVendorHardwareData.success, (state, { vendorHardwareData }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        vendorHardwareData: MergeArrayValues(state.vendorHardwareData, vendorHardwareData),
        error: null
    })),
    on(MetalsActions.SetVendorHardwareData.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.LoadProductSetups.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(MetalsActions.LoadProductSetups.success, (state, { productSetups }): MetalsState => ({
        ...state,
        productSetups,
        loadingState: state.loadingState - 1,
    })),
    on(MetalsActions.LoadProductSetups.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    on(MetalsActions.LoadProductSetupById.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(MetalsActions.LoadProductSetupById.success, (state, { productSetup }): MetalsState => ({
        ...state,
        productSetups: MergeArrayValues(state.productSetups, productSetup),
        loadingState: state.loadingState - 1,
    })),
    on(MetalsActions.LoadProductSetupById.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    on(MetalsActions.SetProductSetup.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(MetalsActions.SetProductSetup.success, (state, { productSetup }): MetalsState => ({
        ...state,
        productSetups: MergeArrayValues(state.productSetups, productSetup),
        loadingState: Math.max(0, state.loadingState - 1),
    })),
    on(MetalsActions.SetProductSetup.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: Math.max(0, state.loadingState - 1),
        error,
    })),
    on(MetalsActions.UpsertProductSetupLocal, (state, { productSetup }): MetalsState => ({
        ...state,
        productSetups: MergeArrayValues(state.productSetups, productSetup),
    })),
    on(MetalsActions.DeleteProductSetup.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(MetalsActions.DeleteProductSetup.success, (state, { productSetupId }): MetalsState => ({
        ...state,
        productSetups: state.productSetups.filter((setup) => setup.id !== productSetupId),
        loadingState: state.loadingState - 1,
    })),
    on(MetalsActions.DeleteProductSetup.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    // =========================================================
    on(MetalsActions.LoadToolRoomMachines.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(MetalsActions.LoadToolRoomMachines.success, (state, { machines }): MetalsState => ({
        ...state,
        toolRoomMachines: machines,
        loadingState: state.loadingState - 1,
    })),
    on(MetalsActions.LoadToolRoomMachines.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),
    on(MetalsActions.SetToolRoomMachine.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(MetalsActions.SetToolRoomMachine.success, (state, { machine }): MetalsState => ({
        ...state,
        toolRoomMachines: MergeArrayValues(state.toolRoomMachines, machine)
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
        loadingState: Math.max(0, state.loadingState - 1),
    })),
    on(MetalsActions.SetToolRoomMachine.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: Math.max(0, state.loadingState - 1),
        error,
    })),
    on(MetalsActions.DeleteToolRoomMachine.initial, (state): MetalsState => ({
        ...state,
        loadingState: state.loadingState + 1,
        error: null,
    })),
    on(MetalsActions.DeleteToolRoomMachine.success, (state, { machineId }): MetalsState => ({
        ...state,
        toolRoomMachines: state.toolRoomMachines.filter((machine) => machine.id !== machineId),
        loadingState: state.loadingState - 1,
    })),
    on(MetalsActions.DeleteToolRoomMachine.failure, (state, { error }): MetalsState => ({
        ...state,
        loadingState: state.loadingState - 1,
        error,
    })),

)
